import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { 
  Save, 
  Eye, 
  Edit3,
  Upload,
  X,
  Plus,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = `${BACKEND_URL}/api`;

const LandingEditor = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'administrativo') {
      navigate('/');
      return;
    }
    loadConfig();
  }, [user, navigate]);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/landing-config`, {
        withCredentials: true
      });
      
      // Set default config if empty, otherwise merge defaults with DB to preserve missing fields
      if (!response.data || Object.keys(response.data).length === 0) {
        setConfig(getDefaultConfig());
      } else {
        setConfig({ ...getDefaultConfig(), ...response.data });
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setConfig(getDefaultConfig());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultConfig = () => ({
    brandName: 'w agencia',
    brandTagline: 'Transformamos modelos en marcas rentables',
    logoUrl: '',
    heroTitle: 'La agencia #1 de OnlyFans',
    heroSubtitle: 'Convertimos creadoras en marcas rentables. ¡Únete y forma parte del top 0,1 % de creadoras!',
    heroImage: 'https://images.unsplash.com/photo-1613909671501-f9678ffc1d33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmYXNoaW9uJTIwbW9kZWx8ZW58MHx8fHwxNzY0ODE4OTEwfDA&ixlib=rb-4.1.0&q=85',
    heroCTAText: 'Aplicar ahora',
    modelsCount: '22+',
    modelsText: 'Creadoras confían en nosotros',
    topPercentage: 'Top 0.1%',
    topPercentageText: 'De creadoras exitosas',
    quoteText: 'Fundada por una mujer que conoce la industria, W Agencia representa apoyo genuino, crecimiento inteligente y éxito sostenible.',
    quoteAuthor: 'Louisa N.',
    quoteRole: 'Fundadora, w agency',
    whySectionTitle: '¿Por qué elegirnos?',
    whySectionSubtitle: 'por mujeres, para mujeres',
    whySection: [
      {
        title: 'Te protegemos',
        description: 'Lo que no publiquemos se mantiene privado. Eliminamos filtraciones antes de que se difundan.',
        image: 'https://images.unsplash.com/photo-1762843353685-0d85c8c882f3'
      },
      {
        title: 'Somos más rápidos',
        description: 'Mientras dormís, nosotros trabajamos en tu éxito. Nuestro equipo gestiona tus chats 24/7.',
        image: 'https://images.unsplash.com/photo-1599692545152-92c37e658e0f'
      },
      {
        title: 'Estamos para vos',
        description: 'W Agencia fue fundada por una creadora exitosa. Una amiga que sabe cómo se siente cada etapa.',
        image: 'https://images.unsplash.com/photo-1571513800374-df1bbe650e56'
      }
    ],
    faqTitle: 'Preguntas frecuentes',
    faqs: [
      {
        question: '¿Cómo funciona la colaboración?',
        answer: 'Después de aplicar, evaluamos tu potencial. Si encaja, comenzamos con una incorporación personalizada.'
      }
    ],
    finalCtaTitle: 'Solo aceptamos modelos en las que creemos.',
    finalCtaHighlight: '¿Vas a ser vos?',
    finalCtaImage: 'https://images.unsplash.com/photo-1677083969180-39331b300b6c',
    accentColor: '#c8784e',
    accentColorHover: '#d89a6f',
    contactEmail: 'support@wonlyfansagencia.com',
    applicationFormUrl: 'https://airtable.com/app3dZnyndIsyAqAW/pagLpklnZkm1n0cd0/form',
    socialMedia: [
      { platform: 'instagram', url: '', icon: 'instagram' },
      { platform: 'twitter', url: '', icon: 'twitter' },
      { platform: 'tiktok', url: '', icon: 'tiktok' }
    ]
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/landing-config`, config, {
        withCredentials: true
      });
      toast.success('✅ Cambios guardados exitosamente');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, fieldPath) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      // Update config with new image URL
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      updateConfigField(fieldPath, imageUrl);
      toast.success('Imagen subida exitosamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir imagen');
    }
  };

  const updateConfigField = (path, value) => {
    const newConfig = { ...config };
    const keys = path.split('.');
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
  };

  const addFAQ = () => {
    setConfig({
      ...config,
      faqs: [...(config.faqs || []), { question: 'Nueva pregunta', answer: 'Nueva respuesta' }]
    });
  };

  const removeFAQ = (index) => {
    const newFaqs = config.faqs.filter((_, i) => i !== index);
    setConfig({ ...config, faqs: newFaqs });
  };

  const addSocialMedia = () => {
    setConfig({
      ...config,
      socialMedia: [...(config.socialMedia || []), { platform: 'instagram', url: '', icon: 'instagram' }]
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando editor...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Error al cargar configuración</div>
      </div>
    );
  }

  const accentColor = config.accentColor || '#c8784e';

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 bg-black border-b border-gray-800 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-white">Modo Edición</h1>
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="outline"
                size="sm"
                className="border-gray-700 text-white"
              >
                <Settings className="mr-2 h-4 w-4" />
                Configuración
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={() => window.open('/', '_blank')}
                variant="outline"
                size="sm"
                className="border-gray-700 text-white"
              >
                <Eye className="mr-2 h-4 w-4" />
                Vista Previa
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                style={{ backgroundColor: accentColor }}
                className="text-black"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
              
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="border-gray-700 text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Sidebar */}
      {showSettings && (
        <div className="fixed top-16 left-0 bottom-0 w-80 bg-gray-800 border-r border-gray-700 z-40 overflow-y-auto p-4">
          <h3 className="text-white font-bold mb-4">Configuración General</h3>
          
          <div className="space-y-4">
            {/* Brand */}
            <div>
              <Label className="text-gray-300">Nombre de Agencia</Label>
              <Input
                value={config.brandName}
                onChange={(e) => setConfig({ ...config, brandName: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <Label className="text-gray-300">Logo</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0], 'logoUrl')}
                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white hover:file:bg-gray-600"
              />
              {config.logoUrl && (
                <img src={config.logoUrl} alt="Logo" className="mt-2 h-12 object-contain" />
              )}
            </div>

            {/* Colors */}
            <div>
              <Label className="text-gray-300">Color Principal</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={config.accentColor}
                  onChange={(e) => setConfig({ ...config, accentColor: e.target.value })}
                  className="w-12 h-10 bg-gray-700 border-gray-600"
                />
                <Input
                  value={config.accentColor}
                  onChange={(e) => setConfig({ ...config, accentColor: e.target.value })}
                  className="flex-1 bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Social Media */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-300">Redes Sociales</Label>
                <Button onClick={addSocialMedia} size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {config.socialMedia && config.socialMedia.map((social, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={social.platform}
                    onChange={(e) => {
                      const newSocial = [...config.socialMedia];
                      newSocial[index].platform = e.target.value;
                      setConfig({ ...config, socialMedia: newSocial });
                    }}
                    placeholder="Plataforma"
                    className="w-1/3 bg-gray-700 border-gray-600 text-white text-sm"
                  />
                  <Input
                    value={social.url}
                    onChange={(e) => {
                      const newSocial = [...config.socialMedia];
                      newSocial[index].url = e.target.value;
                      setConfig({ ...config, socialMedia: newSocial });
                    }}
                    placeholder="URL"
                    className="flex-1 bg-gray-700 border-gray-600 text-white text-sm"
                  />
                </div>
              ))}
            </div>

            {/* Email */}
            <div>
              <Label className="text-gray-300">Email de Contacto</Label>
              <Input
                value={config.contactEmail}
                onChange={(e) => setConfig({ ...config, contactEmail: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>

            {/* Application Form URL */}
            <div>
              <Label className="text-gray-300">URL Formulario</Label>
              <Input
                value={config.applicationFormUrl}
                onChange={(e) => setConfig({ ...config, applicationFormUrl: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Landing Preview with Editable Sections */}
      <div className={`pt-16 ${showSettings ? 'ml-80' : ''} transition-all`}>
        <div className="relative">
          
          {/* HERO SECTION - Editable */}
          <section 
            className="relative min-h-screen flex items-center justify-center cursor-pointer group"
            onClick={() => setEditingSection(editingSection === 'hero' ? null : 'hero')}
          >
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#1d1e20] z-10" />
              <img
                src={config.heroImage}
                alt="Hero"
                className="w-full h-full object-cover opacity-40"
              />
              {editingSection !== 'hero' && (
                <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                    <Edit3 className="mr-2 h-4 w-4" />
                    Editar Hero
                  </Button>
                </div>
              )}
            </div>

            <div className="container mx-auto px-4 relative z-20 text-center">
              {editingSection === 'hero' ? (
                <div className="max-w-2xl mx-auto bg-black/80 p-6 rounded-lg space-y-4">
                  <Input
                    value={config.heroTitle}
                    onChange={(e) => setConfig({ ...config, heroTitle: e.target.value })}
                    className="text-4xl font-bold bg-gray-800 border-gray-700 text-white"
                    placeholder="Título principal"
                  />
                  <Textarea
                    value={config.heroSubtitle}
                    onChange={(e) => setConfig({ ...config, heroSubtitle: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                    placeholder="Subtítulo"
                  />
                  <div>
                    <Label className="text-white">Imagen de Fondo</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'heroImage')}
                      className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white"
                    />
                  </div>
                  <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                    Cerrar
                  </Button>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto">
                  <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight text-white">
                    {config.heroTitle}
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light">
                    {config.heroSubtitle}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* SOCIAL PROOF SECTION */}
          <section 
            className="py-16 bg-black cursor-pointer group relative"
            onClick={() => setEditingSection(editingSection === 'social' ? null : 'social')}
          >
            {editingSection !== 'social' && (
              <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                  <Edit3 className="mr-2 h-4 w-4" />
                  Editar Números
                </Button>
              </div>
            )}
            
            {editingSection === 'social' ? (
              <div className="container mx-auto px-4 max-w-2xl bg-gray-900 p-6 rounded-lg space-y-4">
                <h3 className="text-white font-bold mb-4">Editar Prueba Social</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Número Modelos</Label>
                    <Input
                      value={config.modelsCount}
                      onChange={(e) => setConfig({ ...config, modelsCount: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Texto</Label>
                    <Input
                      value={config.modelsText}
                      onChange={(e) => setConfig({ ...config, modelsText: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Porcentaje Top</Label>
                    <Input
                      value={config.topPercentage}
                      onChange={(e) => setConfig({ ...config, topPercentage: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Texto Porcentaje</Label>
                    <Input
                      value={config.topPercentageText}
                      onChange={(e) => setConfig({ ...config, topPercentageText: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                  Cerrar
                </Button>
              </div>
            ) : (
              <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#c8784e]/10 p-4 rounded-full">
                      <span className="text-[#c8784e] text-2xl">👥</span>
                    </div>
                    <div>
                      <p className="text-4xl font-bold" style={{ color: accentColor }}>{config.modelsCount}</p>
                      <p className="text-gray-400 font-light">{config.modelsText}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="bg-[#c8784e]/10 p-4 rounded-full">
                      <span className="text-[#c8784e] text-2xl">📈</span>
                    </div>
                    <div>
                      <p className="text-4xl font-bold" style={{ color: accentColor }}>{config.topPercentage}</p>
                      <p className="text-gray-400 font-light">{config.topPercentageText}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* QUOTE SECTION */}
          <section 
            className="py-24 bg-[#1d1e20] cursor-pointer group relative"
            onClick={() => setEditingSection(editingSection === 'quote' ? null : 'quote')}
          >
            {editingSection !== 'quote' && (
              <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                  <Edit3 className="mr-2 h-4 w-4" />
                  Editar Cita
                </Button>
              </div>
            )}
            
            {editingSection === 'quote' ? (
              <div className="container mx-auto px-4 max-w-2xl bg-gray-900 p-6 rounded-lg space-y-4">
                <h3 className="text-white font-bold mb-4">Editar Cita</h3>
                <Textarea
                  value={config.quoteText}
                  onChange={(e) => setConfig({ ...config, quoteText: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={4}
                />
                <Input
                  value={config.quoteAuthor}
                  onChange={(e) => setConfig({ ...config, quoteAuthor: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Autor"
                />
                <Input
                  value={config.quoteRole}
                  onChange={(e) => setConfig({ ...config, quoteRole: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Rol"
                />
                <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                  Cerrar
                </Button>
              </div>
            ) : (
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl md:text-4xl font-light mb-8 leading-relaxed italic text-gray-200">
                    {config.quoteText}
                  </h2>
                  <div className="flex flex-col items-center gap-4">
                    <p className="text-xl font-semibold" style={{ color: accentColor }}>{config.quoteAuthor}</p>
                    <p className="text-gray-400 font-light">{config.quoteRole}</p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* WHY CHOOSE US SECTION */}
          <section className="py-24 bg-black">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <p className="uppercase tracking-wider mb-4 font-semibold" style={{ color: accentColor }}>
                  {config.whySectionSubtitle}
                </p>
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                  {config.whySectionTitle}
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {config.whySection && config.whySection.map((item, index) => (
                  <div
                    key={index}
                    className="bg-[#1d1e20] rounded-2xl p-8 cursor-pointer hover:bg-[#252628] transition-all group relative"
                    onClick={() => setEditingSection(editingSection === `why-${index}` ? null : `why-${index}`)}
                  >
                    {editingSection !== `why-${index}` && (
                      <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                          <Edit3 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    
                    {editingSection === `why-${index}` ? (
                      <div className="space-y-4">
                        <Input
                          value={item.title}
                          onChange={(e) => {
                            const newSection = [...config.whySection];
                            newSection[index].title = e.target.value;
                            setConfig({ ...config, whySection: newSection });
                          }}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Título"
                        />
                        <Textarea
                          value={item.description}
                          onChange={(e) => {
                            const newSection = [...config.whySection];
                            newSection[index].description = e.target.value;
                            setConfig({ ...config, whySection: newSection });
                          }}
                          className="bg-gray-800 border-gray-700 text-white"
                          rows={4}
                          placeholder="Descripción"
                        />
                        <div>
                          <Label className="text-white">Imagen</Label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e.target.files[0], `whySection.${index}.image`)}
                            className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white"
                          />
                        </div>
                        <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                          Cerrar
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${accentColor}20` }}>
                          <span className="text-3xl">✨</span>
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-white">{item.title}</h3>
                        <p className="text-gray-400 leading-relaxed font-light">{item.description}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ SECTION */}
          <section className="py-24 bg-[#1d1e20]">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <p className="uppercase tracking-wider mb-4 font-semibold" style={{ color: accentColor }}>FAQ'S</p>
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                  {config.faqTitle}
                </h2>
              </div>

              <div className="max-w-3xl mx-auto space-y-4">
                {config.faqs && config.faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-black border border-gray-800 rounded-lg p-6 cursor-pointer hover:border-gray-700 transition-colors group relative"
                    onClick={() => setEditingSection(editingSection === `faq-${index}` ? null : `faq-${index}`)}
                  >
                    {editingSection !== `faq-${index}` && (
                      <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                          <Edit3 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    
                    {editingSection === `faq-${index}` ? (
                      <div className="space-y-4">
                        <Input
                          value={faq.question}
                          onChange={(e) => {
                            const newFaqs = [...config.faqs];
                            newFaqs[index].question = e.target.value;
                            setConfig({ ...config, faqs: newFaqs });
                          }}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Pregunta"
                        />
                        <Textarea
                          value={faq.answer}
                          onChange={(e) => {
                            const newFaqs = [...config.faqs];
                            newFaqs[index].answer = e.target.value;
                            setConfig({ ...config, faqs: newFaqs });
                          }}
                          className="bg-gray-800 border-gray-700 text-white"
                          rows={3}
                          placeholder="Respuesta"
                        />
                        <div className="flex gap-2">
                          <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                            Cerrar
                          </Button>
                          <Button onClick={() => removeFAQ(index)} variant="destructive" size="sm">
                            <X className="h-4 w-4 mr-2" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                        <p className="text-gray-400">{faq.answer}</p>
                      </>
                    )}
                  </div>
                ))}
                
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    addFAQ();
                  }}
                  className="w-full"
                  style={{ backgroundColor: accentColor }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Pregunta
                </Button>
              </div>
            </div>
          </section>

          {/* FINAL CTA SECTION */}
          <section 
            className="relative py-32 cursor-pointer group"
            onClick={() => setEditingSection(editingSection === 'finalcta' ? null : 'finalcta')}
          >
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black z-10" />
              <img
                src={config.finalCtaImage}
                alt="Final CTA"
                className="w-full h-full object-cover opacity-30"
              />
              {editingSection !== 'finalcta' && (
                <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" style={{ backgroundColor: accentColor }} className="text-black">
                    <Edit3 className="mr-2 h-4 w-4" />
                    Editar CTA Final
                  </Button>
                </div>
              )}
            </div>

            <div className="container mx-auto px-4 relative z-20">
              {editingSection === 'finalcta' ? (
                <div className="max-w-2xl mx-auto bg-black/80 p-6 rounded-lg space-y-4">
                  <Input
                    value={config.finalCtaTitle}
                    onChange={(e) => setConfig({ ...config, finalCtaTitle: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white text-xl"
                    placeholder="Título"
                  />
                  <Input
                    value={config.finalCtaHighlight}
                    onChange={(e) => setConfig({ ...config, finalCtaHighlight: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Texto destacado"
                  />
                  <div>
                    <Label className="text-white">Imagen de Fondo</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'finalCtaImage')}
                      className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-700 file:text-white"
                    />
                  </div>
                  <Button onClick={() => setEditingSection(null)} style={{ backgroundColor: accentColor }} className="text-black">
                    Cerrar
                  </Button>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-12 leading-tight text-white">
                    {config.finalCtaTitle} <br />
                    <span style={{ color: accentColor }}>{config.finalCtaHighlight}</span>
                  </h2>
                </div>
              )}
            </div>
          </section>

          {/* FOOTER */}
          <footer className="bg-black py-12 border-t border-gray-900">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <div className="text-2xl font-bold mb-2" style={{ color: accentColor }}>
                    {config.brandName}
                  </div>
                  <p className="text-gray-400 font-light italic">{config.brandTagline}</p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-gray-400">📧</span>
                  <span className="text-gray-400">{config.contactEmail}</span>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-900 text-center">
                <p className="text-gray-500 text-sm font-light">
                  © 2024 {config.brandName}. Todos los derechos reservados.
                </p>
              </div>
            </div>
          </footer>

        </div>
      </div>
    </div>
  );
};

export default LandingEditor;
